import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { createSelector } from "reselect"
import moment from "moment-timezone"
import { getCurrentAgenda } from "../../../router/selectors"
import { getReportsStateObject } from "../selectors"
import type { StateObject } from "../../../redux/types"
import type { CashReportPagesObject, CashReportState } from "../reducer"
import {
    getFilters,
    getSelectedLocationIdsFromGlobalFilter,
    getTimezoneForQueryParam,
} from "../../../filters/selectors"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import { MainMenuAgendaEnum } from "../../../router/types"
import { formatDateTime } from "../../../../utils/formatters"
import { getMidnightOffset } from "../../settings/selectors"
import type { CashReportItem, CashReportRequestData } from "../../../../graphql/types"

export const getCashReportState = (state: StateObject): CashReportState => getReportsStateObject(state).cash

export const getCashReportPages = (state: StateObject): CashReportPagesObject => getCashReportState(state).pages

export const getCashReportCurrentPage = (state: StateObject): number => getFilters(state, "cash").pagination.currentPage

export const getCashReportItemsByPageNumber = (state: StateObject, page: number): Maybe<CashReportItem[]> =>
    getCashReportPages(state)[page]

export const getCashReportItemsInCurrentPage: (arg0: StateObject) => CashReportItem[] = createSelector(
    getCashReportPages,
    getCashReportCurrentPage,
    (pages: CashReportPagesObject, current: number) => pages[current] || []
)

export const isCashReportAgendaOpened = (state: StateObject) =>
    getCurrentAgenda(state) === MainMenuAgendaEnum.CashRegisterReport

export const getDataForCashReportTable = (state: StateObject): CashReportItem[] =>
    getCashReportItemsInCurrentPage(state)

export const getCashReportQueryApiParam = (state: StateObject): CashReportRequestData => {
    const globalFilters = getFilters(state, GLOBAL_FILTER_IDENTIFIER)
    const filters = getFilters(state, "cash")
    const selectedLocationIds = getSelectedLocationIdsFromGlobalFilter(state)

    const offset = (filters.pagination.currentPage - 1) * filters.pagination.pageLimit
    const midnightOffset = getMidnightOffset(state)
    const from = midnightOffset
        ? moment(globalFilters.timestamp.from).subtract(midnightOffset, "m").format("YYYY-MM-DD")
        : globalFilters.timestamp &&
          globalFilters.timestamp.from &&
          formatDateTime(globalFilters.timestamp.from, "YYYY-MM-DD")
    const to = midnightOffset
        ? moment(globalFilters.timestamp.to).subtract(midnightOffset, "m").format("YYYY-MM-DD")
        : globalFilters.timestamp &&
          globalFilters.timestamp.to &&
          formatDateTime(globalFilters.timestamp.to, "YYYY-MM-DD")
    return {
        from: from,
        to: to,
        timezone: getTimezoneForQueryParam(state, selectedLocationIds),
        location_ids: selectedLocationIds.length > 0 ? selectedLocationIds.join() : "",
        ...(filters.sort && filters.sort.by
            ? {
                  sort: filters.sort.by.toLowerCase(),
              }
            : null),
        ...(filters.sort && filters.sort.direction
            ? {
                  sort_order: filters.sort.direction,
              }
            : null),
        ...(filters.pay_in_pay_out && filters.pay_in_pay_out.length > 0
            ? {
                  direction: filters.pay_in_pay_out.join(),
              }
            : null),
        ...(filters.operation && filters.operation.length > 0
            ? {
                  operation: filters.operation.join(),
              }
            : null),
        offset: offset,
        count: filters.pagination.pageLimit,
    }
}
